import { GRID_PAGE_SIZES } from "../lib/list-window";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import FilterAltOffOutlinedIcon from "@mui/icons-material/FilterAltOffOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import SortIcon from "@mui/icons-material/Sort";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LinkIcon from "@mui/icons-material/Link";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import TableRowsIcon from "@mui/icons-material/TableRows";
import { IconButton, Menu, MenuItem, Tooltip, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  DataGrid,
  GridColDef,
  GridColumnOrderChangeParams,
  GridPaginationModel,
  GridRowSelectionModel,
  GridSortModel,
} from "@mui/x-data-grid";
import { toast } from "react-hot-toast";
import { getSchema } from "oschema";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { measureGridHostCap } from "../lib/grid-host-cap";
import { overviewColumnsToHide } from "../lib/overview-header-fit";
import { overviewRowClipShrink } from "../lib/overview-row-fit";
import { useDynamicForm } from "../lib/useDynamicForm";
import {
  Field,
  columnRank,
  copyRowForCreate,
  fieldLabel,
  inputKindFor,
  isBooleanField,
  isIdentityField,
  isNumeric,
  isPrimaryKey,
  isReadOnlyField,
  isSecretField,
  isStructured,
  isTemporal,
} from "../lib/schema-field";
import { formatNumber, formatValue } from "../lib/format";
import { menuItemSx, menuPaperSx } from "../lib/menu-chrome";
import { rowKey, rowKeyOrHash } from "../lib/row-key";
import type { SyncChange } from "../lib/sync-state";
import { SyncStatusMarker } from "./sync/SyncStatusMarker";
import type { DrawerTarget } from "../lib/drawer-url";
import { validateRecord } from "../lib/validate-record";
import {
  ContextMenu,
  ContextMenuEntry,
  ContextMenuPosition,
} from "./ContextMenu";
import { DataCell } from "./DataCell";
import { DataTablePagination } from "./DataTablePagination";
import { gridColEditProps, isUnsafeInlineEdit } from "./GridEditCell";
import { MobilePageBar, MobileRecordList } from "./MobileRecordList";
import { RowDetailModal } from "./RowDetailModal";
import { Button } from "./ui/Button";

export type TableDensity = "compact" | "standard" | "comfortable";

const ROW_HEIGHT: Record<TableDensity, number> = {
  compact: 36,
  standard: 52,
  comfortable: 67,
};

const GRID_HEADER_H = 40;
const GRID_FOOTER_H = 44;
const COMPACT_GRID_FOOTER_H = 36;
const GRID_SCROLL_PAD = 10;
const EMPTY_BODY_MIN = 120;
const GRID_CELL_PADDING_X = 10;
const GRID_SINGLE_CELL_PADDING_Y = 6;
const GRID_CELL_BORDER = 1;

function gridMetrics(
  density: TableDensity,
  touchTargets: boolean,
  isSingleRow: boolean,
  desktopPagination: boolean,
  hideFooter: boolean
) {
  const target = touchTargets ? 44 : 32;
  return {
    rowHeight: Math.max(ROW_HEIGHT[density], touchTargets ? Math.max(48, target + GRID_CELL_BORDER + (isSingleRow ? GRID_SINGLE_CELL_PADDING_Y * 2 : 0)) : 0),
    headerHeight: touchTargets ? Math.max(GRID_HEADER_H, 48) : GRID_HEADER_H,
    footerHeight: isSingleRow || hideFooter ? 0 : touchTargets ? target + (desktopPagination ? 12 : 0) : desktopPagination ? GRID_FOOTER_H : COMPACT_GRID_FOOTER_H,
    actionWidth: touchTargets ? target + GRID_CELL_PADDING_X * 2 + GRID_CELL_BORDER : 48,
  };
}

/** Header + current page rows + footer. Cap against the pane separately. */
function gridHugHeight(
  rowCount: number,
  metrics: ReturnType<typeof gridMetrics>,
  isSingleRow: boolean
): number {
  const { rowHeight, headerHeight, footerHeight } = metrics;
  const body = rowCount === 0 ? EMPTY_BODY_MIN : rowCount * rowHeight;
  if (isSingleRow) return headerHeight + body;
  return headerHeight + body + footerHeight + GRID_SCROLL_PAD;
}

type DynamicDataTableProps = {
  fields?: Field[];
  samples?: any[];
  data?: any[];
  loading: boolean;
  total: number;
  onEdit?: (row: any) => void;
  idField?: string;
  paginationChange?: (paginationModel: GridPaginationModel) => void;
  paginationMode?: "client" | "server";
  onRowClick?: (row: any) => void;
  onRowDoubleClick?: (row: any, event?: any, details?: any) => void;
  sortModel?: GridSortModel;
  onSortModelChange?: (sortModel: GridSortModel) => void;
  onRowUpdate?: (row: any, original?: any) => void;
  onDelete?: (row: any) => void;
  /** Right-click "filter by this value". Omitted, the filter entries are hidden. */
  onAddFilter?: (
    field: string,
    operator: string,
    value: string | boolean
  ) => void;
  /** Selected row ids. Supplying a bulk callback turns the checkbox column on. */
  selectedIds?: GridRowSelectionModel;
  onSelectedIdsChange?: (ids: GridRowSelectionModel) => void;
  onBulkDelete?: () => void;
  onBulkEdit?: () => void;
  onBulkExport?: () => void;
  /** Gmail-style: page the current filter set and select those ids (capped). */
  onSelectMatching?: () => void;
  selectMatchingPending?: boolean;
  matchingSelectCap?: number;
  debugMode?: boolean;
  datasourceId?: string;
  collection?: string;
  paginationModel?: GridPaginationModel;
  onRefresh?: () => void;
  onDrawerTarget?: (target: DrawerTarget) => void;
  viewMode?: "single-row" | "normal";
  /**
   * Overview card preview: no pagination footer, no mobile page bar, no
   * checkbox, and no row menu. Those controls need the collection toolbar,
   * which the card does not mount. The collection page leaves this false.
   */
  hidePagination?: boolean;
  expandableFields?: string[]; // relation paths currently expanded
  onExpansionChange?: (relationNames: string[]) => void;
  expandMode?: "inline" | "row" | "popover";
  columnVisibility: { [key: string]: boolean };
  onColumnVisibilityChange: (newModel: { [key: string]: boolean }) => void;
  columnOrder?: string[];
  onColumnOrderChange?: (newOrder: string[]) => void;
  expandableFieldsMap?: Map<
    string,
    { relationName: string; path: string; level: number; parentPath?: string }
  >;
  density?: TableDensity;
  /** Demo datasource: full write UI, but every write is simulated. */
  demoMode?: boolean;
  /** Non-demo read-only datasource: hide all write UI. */
  readOnly?: boolean;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  emptySuppressed?: boolean;
  /**
   * Replaces the empty-grid copy. A broken row filter uses this so the
   * member sees why the grid has 0 rows.
   */
  accessNotice?: string | null;
  error?: string | null;
  /** Sync state per row id (ARD 9.6). Makes the status column. */
  syncChanges?: Map<string, SyncChange>;
  /** False until the first status answer; the column waits for it. */
  syncReady?: boolean;
  /** Google file id, for "Open in Google Sheets" in the failure popover. */
  syncSpreadsheetId?: string | null;
  onSyncRetry?: (changeId: string) => void;
  onSyncDismiss?: (changeId: string) => void;
};

const defaultProps: Partial<DynamicDataTableProps> = {
  paginationMode: "server",
  debugMode: false,
  expandMode: "inline",
  density: "comfortable",
};

/* ------------------------------------------------------------ column sizing */

/**
 * Trailing spacer so the last data column never sits flush against the
 * container edge. Works in both fill-viewport and horizontal-scroll modes;
 * excluded from reorder persistence.
 */
const GUTTER_FIELD = "__gutter";
const GUTTER_WIDTH = 16;
const gutterColumn: GridColDef = {
  field: GUTTER_FIELD,
  headerName: "",
  flex: 1,
  minWidth: GUTTER_WIDTH,
  sortable: false,
  filterable: false,
  hideable: false,
  resizable: false,
  disableColumnMenu: true,
  disableReorder: true,
  disableExport: true,
  cellClassName: "od-gutter-cell",
  headerClassName: "od-gutter-header",
  renderHeader: () => null,
  renderCell: () => null,
};

const HEADER_CHAR_PX = 8;
const HEADER_PAD_PX = 40;
const HEADER_SORT_ICON_PX = 28;
const VALUE_CHAR_PX = 10;
const VALUE_PAD_PX = 48;
const NUMERIC_MIN_WIDTH = 112;
/** Guest projects show figures like `$210,000.00`; size currency to that. */
const CURRENCY_SAMPLE = 210_000;
const CHIP_MIN_WIDTH = 96;

/**
 * Currency/percentage cells render `formatNumber`, so the floor has to fit the
 * formatted sample rather than a one-size 112. Plain integers stay compact.
 */
const minWidthForNumeric = (field: Field): number => {
  const kind = inputKindFor(field);
  if (kind !== "currency" && kind !== "percentage") return NUMERIC_MIN_WIDTH;

  let sample: number;
  if (kind === "currency") {
    const sampledMax =
      typeof field.max === "number" && Number.isFinite(field.max)
        ? Math.abs(field.max)
        : 0;
    sample = Math.max(CURRENCY_SAMPLE, sampledMax);
  } else {
    sample = (field.max ?? 1) <= 1 ? 1 : 100;
    if (typeof field.max === "number" && Number.isFinite(field.max)) {
      sample = Math.abs(field.max);
    }
  }

  const formatted = formatNumber(sample, field);
  return Math.max(
    NUMERIC_MIN_WIDTH,
    VALUE_CHAR_PX * formatted.length + VALUE_PAD_PX
  );
};

/**
 * `flex` fills leftover width. `flex: 0` must also set `width` on the column
 * (MUI defaults a no-flex column to 100px, which clips `$50,000.00` / `BUDGET`).
 */
type ColSize = { flex: number; minWidth: number };

const sizeFor = (
  field: Field,
  isExpandable: boolean,
  isDesktop: boolean,
  renderedValues: string[],
  compact = false
): ColSize => {
  if (isExpandable) return { flex: 0.4, minWidth: 72 };
  const currencyValues = renderedValues.filter((value) => /^-?\$[\d,.]+$/.test(value));
  if (currencyValues.length > 0) {
    return {
      flex: 0,
      minWidth: VALUE_CHAR_PX * Math.max("$210,000.00".length, ...currencyValues.map((value) => value.length)) + VALUE_PAD_PX,
    };
  }
  if (isPrimaryKey(field)) return { flex: 0.4, minWidth: 72 };
  if (isIdentityField(field))
    return { flex: 1.8, minWidth: isDesktop && !compact ? 180 : 140 };
  if (isBooleanField(field) || inputKindFor(field) === "enum")
    return { flex: 0, minWidth: CHIP_MIN_WIDTH };
  if (isTemporal(field)) return { flex: 0.8, minWidth: 112 };
  if (isNumeric(field))
    return { flex: 0, minWidth: minWidthForNumeric(field) };
  if (isStructured(field)) return { flex: 1.4, minWidth: 200 };
  if ((field.maxLength ?? 0) > 120) return { flex: 2, minWidth: 220 };
  return { flex: 1, minWidth: 168 };
};

const columnBox = (
  size: ColSize,
  headerName: string
): Pick<GridColDef, "flex" | "width" | "minWidth"> => {
  const minWidth = minWidthForHeader(headerName, size.minWidth);
  if (size.flex > 0) return { flex: size.flex, minWidth };
  return { flex: 0, width: minWidth, minWidth };
};

/** Keep headers compact; cap the label floor without reducing the value floor. */
const minWidthForHeader = (
  headerName: string,
  sizeMin: number
): number =>
  Math.max(
    sizeMin,
    Math.min(
      320,
      HEADER_CHAR_PX * headerName.toUpperCase().length +
        HEADER_PAD_PX +
        HEADER_SORT_ICON_PX
    )
  );

/* --------------------------------------------------------------- overlays */

const Overlay: React.FC<{
  icon: React.ReactNode;
  title: string;
  hint?: React.ReactNode;
}> = ({ icon, title, hint }) => (
  <div className="flex h-full flex-col items-center justify-center gap-2 px-6 py-10 text-center">
    <div className="text-od-muted/60">{icon}</div>
    <p className="m-0 text-sm font-medium text-od-text">{title}</p>
    {hint && <div className="m-0 text-xs text-od-muted">{hint}</div>}
  </div>
);

const actionBtnSx = {
  color: "var(--od-muted)",
  "&:hover": {
    color: "var(--od-primary)",
    backgroundColor: "color-mix(in srgb, var(--od-primary) 12%, transparent)",
  },
};

const deleteBtnSx = {
  color: "var(--od-muted)",
  "&:hover": {
    color: "var(--od-danger)",
    backgroundColor: "color-mix(in srgb, var(--od-danger) 12%, transparent)",
  },
};

type RowActionsProps = {
  compact: boolean;
  readOnly?: boolean;
  /** A create that has not landed: no edit, no delete (ARD 9.4). */
  pendingCreate?: boolean;
  onView: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

const RowActions: React.FC<RowActionsProps> = ({
  compact,
  readOnly,
  pendingCreate,
  onView,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (compact) {
    return (
      <div className="od-row-actions flex justify-center">
        <IconButton
          size="small"
          aria-label="Row actions"
          aria-haspopup="menu"
          aria-expanded={!!anchor}
          sx={actionBtnSx}
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
        >
          <MoreVertIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Menu
          anchorEl={anchor}
          open={!!anchor}
          onClose={() => setAnchor(null)}
          onClick={(e) => e.stopPropagation()}
          // The menu portals out, but React bubbles its events to this grid
          // cell. The grid read a press on the menu as a cell click and, on
          // mouseup, focused this cell again, which closed a dropdown just
          // opened in the record form.
          onMouseDown={(e) => e.stopPropagation()}
          onMouseUp={(e) => e.stopPropagation()}
          // No exit fade: Edit opens a drawer at once, and a fading invisible
          // backdrop swallowed the first click in it.
          transitionDuration={{ exit: 0 }}
          slotProps={{ paper: { sx: menuPaperSx } }}
          MenuListProps={{ dense: true, sx: { py: 0 } }}
        >
          <MenuItem
            dense
            onClick={() => {
              setAnchor(null);
              onView();
            }}
            sx={menuItemSx}
          >
            View
          </MenuItem>
          {!readOnly && (
            <MenuItem
              dense
              disabled={pendingCreate}
              onClick={() => {
                setAnchor(null);
                onEdit();
              }}
              sx={menuItemSx}
            >
              Edit
            </MenuItem>
          )}
          {!readOnly && (
            <MenuItem
              dense
              onClick={() => {
                setAnchor(null);
                onDuplicate();
              }}
              sx={menuItemSx}
            >
              Duplicate as new
            </MenuItem>
          )}
          {!readOnly && (
            <MenuItem
              dense
              disabled={pendingCreate}
              onClick={() => {
                setAnchor(null);
                onDelete();
              }}
              sx={{ ...menuItemSx, color: "var(--od-danger)" }}
            >
              Delete
            </MenuItem>
          )}
        </Menu>
      </div>
    );
  }

  return (
    <div className="od-row-actions flex justify-center gap-0.5">
      <Tooltip title="View record">
        <IconButton
          size="small"
          aria-label="View record"
          sx={actionBtnSx}
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
        >
          <VisibilityOutlinedIcon sx={{ fontSize: 17 }} />
        </IconButton>
      </Tooltip>
      {!readOnly && (
        <Tooltip title={pendingCreate ? "Wait for the row to sync" : "Edit record"}>
          <span>
            <IconButton
              size="small"
              aria-label="Edit record"
              disabled={pendingCreate}
              sx={actionBtnSx}
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
      {!readOnly && (
        <Tooltip title="Duplicate as new">
          <IconButton
            size="small"
            aria-label="Duplicate as new"
            sx={actionBtnSx}
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
          >
            <ContentCopyOutlinedIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Tooltip>
      )}
      {!readOnly && (
        <Tooltip title={pendingCreate ? "Wait for the row to sync" : "Delete record"}>
          <span>
            <IconButton
              size="small"
              aria-label="Delete record"
              disabled={pendingCreate}
              sx={deleteBtnSx}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </div>
  );
};

/* ----------------------------------------------------------------- helpers */

const DynamicDataTableComponent: React.FC<DynamicDataTableProps> = (
  inputProps
) => {
  const props = { ...defaultProps, ...inputProps };
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const gridHostRef = useRef<HTMLDivElement>(null);
  const [gridHostReady, setGridHostReady] = useState(false);
  /**
   * ARD 9.6 accessibility: the DataGrid owns the key events of the cell it
   * renders the marker in. When the failure popover is open, its keys must not
   * reach the grid. The marker reports open and closed here, and the capture
   * handler below stops Escape, Tab and the arrows from changing cells.
   */
  const syncPopoverOpenRef = useRef(false);
  const setSyncPopoverOpen = useCallback((open: boolean) => {
    syncPopoverOpenRef.current = open;
  }, []);
  const [overviewClipHides, setOverviewClipHides] = useState<string[]>([]);
  const isSingleRow = props.viewMode === "single-row";
  const theme = useTheme();
  const desktopPagination = useMediaQuery(theme.breakpoints.up("sm"), {
    noSsr: true,
  });
  const touchTargets = useMediaQuery("(max-width: 639px), (pointer: coarse)", { noSsr: true });
  const metrics = gridMetrics(props.density ?? "comfortable", touchTargets, isSingleRow, desktopPagination, !!props.hidePagination);
  const { rowHeight, headerHeight, actionWidth } = metrics;
  const compactActions = true;
  // Collection ORs demo into readOnly so + New record stays off. Row edit /
  // duplicate / delete chrome still shows; interceptors toast and skip writes.
  const hideWriteChrome = !!props.readOnly && !props.demoMode;
  // A create that has not landed has no row number yet (ARD 9.4). Google gets
  // its row position only when the append is in the sheet, so an edit or a
  // delete would target the wrong row.
  const hasUnsyncedCreate = useCallback(
    (row: any): boolean =>
      !!row && typeof row === "object" && "_rowNumber" in row && row._rowNumber === null,
    []
  );
  const [detailRow, setDetailRow] = useState<any | null>(null);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>(
    props.paginationModel || { pageSize: 25, page: 0 }
  );

  useEffect(() => {
    if (props.paginationModel) setPaginationModel(props.paginationModel);
  }, [props.paginationModel]);

  const columnVisibility = props.columnVisibility;
  const columnOrder = props.columnOrder || [];

  const [expandedFields, setExpandedFields] = useState<Set<string>>(
    () => new Set(props.expandableFields || [])
  );

  useEffect(() => {
    setExpandedFields(new Set(props.expandableFields || []));
  }, [props.expandableFields]);

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPaginationModel(model);
    props.paginationChange?.(model);
  };

  const schema = useMemo<Field[]>(() => {
    if (props.fields) return props.fields;
    if (props.samples?.length) return getSchema(props.samples) as Field[];
    if (props.data?.length) return getSchema(props.data) as Field[];
    return [];
  }, [props.fields, props.samples, props.data]);

  const idField = useMemo(() => {
    if (props.idField) return props.idField;

    for (const candidate of ["id", "_id", "uuid", "guid"]) {
      if (schema.find((p) => p.name === candidate)) return candidate;
    }

    const primary = schema.find((f) => (f as Field).primary);
    if (primary) return primary.name;

    // A keyless table (a Google Sheet, a Postgres table with no primary key)
    // gets a synthetic key from the api. It is unique and stable across
    // pages, so it beats the name heuristics below, which can pick a
    // non-unique column or flip between candidates as the page changes.
    const first = (props.data || [])[0];
    if (first) {
      for (const synthetic of ["_syntheticId", "_ctid", "_rowNumber"]) {
        if (first[synthetic] !== undefined && first[synthetic] !== null) return synthetic;
      }
    }

    if (props.collection) {
      const camel = `${props.collection.charAt(0).toLowerCase()}${props.collection.slice(1)}Id`;
      if (schema.find((p) => p.name === camel)) return camel;
      const lower = `${props.collection.toLowerCase()}id`;
      const match = schema.find((p) => p.name.toLowerCase() === lower);
      if (match) return match.name;
    }

    const rows = props.data || [];
    if (rows.length > 0) {
      const candidates = schema
        .filter((f) => f.name.endsWith("Id") || f.name.endsWith("_id"))
        .map((f) => f.name);
      for (const candidate of candidates) {
        const values = rows.map((row) => row[candidate]);
        if (
          new Set(values).size === rows.length &&
          !values.includes(null) &&
          !values.includes(undefined)
        ) {
          return candidate;
        }
      }

    }

    // No key at all: let rowKeyOrHash fall back to a content hash rather
    // than treating the first column as an identity.
    return undefined;
  }, [props.data, props.idField, props.collection, schema]);

  const { FormModal, openForm } = useDynamicForm({
    title: props.collection || "Record",
    datasourceId: props.datasourceId!,
    collection: props.collection!,
    schema: props.fields,
    demoMode: props.demoMode,
    pageRows: props.data,
    rowCount: props.total,
    paginationModel,
    onPageChange: (page) =>
      onPaginationModelChange({ ...paginationModel, page }),
    pageLoading: props.loading,
    idField,
    rowIdOf: (row) => rowKeyOrHash(row, idField),
    urlRowIdOf: (row) => rowKey(row, idField),
    onDrawerTarget: props.onDrawerTarget,
    canDelete: !props.readOnly,
    onSubmit: () => {
      props.onRefresh?.();
    },
  });


  const getDisplayableFields = useCallback((obj: any): string[] => {
    if (!obj || typeof obj !== "object") return [];
    const priority = ["name", "title", "label", "status", "email", "description"];
    const available = Object.keys(obj);
    const ordered = [...priority];
    available.forEach((f) => {
      if (!ordered.includes(f)) ordered.push(f);
    });
    return ordered
      .filter((f) => available.includes(f))
      // Expanding a relation must not print what the owning table hides: a
      // `user › password` sub-column leaks exactly the value the parent grid
      // drops. Filtering here also keeps the secret out of the flattened row,
      // so it never reaches the row model at all.
      .filter((f) => !isSecretField(f));
  }, []);

  const duplicateRow = useCallback(
    (row: any) => {
      openForm(copyRowForCreate(row, schema), { isUpdate: false });
    },
    [openForm, schema]
  );

  /**
   * `processRowUpdate` is the only hook that sees the edited value. The old
   * `onCellEditStop` handler read `params.value`, which is the value still in
   * the row model, so it always equalled the previous one and the guard below
   * discarded every edit: typing in a cell saved nothing and the cell reverted.
   *
   * Returning the row commits it to the grid; throwing reverts it, which is why
   * a validation failure has to throw rather than return `oldRow`.
   */
  const handleProcessRowUpdate = useCallback(
    (newRow: any, oldRow: any) => {
      const changed = Object.keys(newRow).filter(
        (key) => !Object.is(newRow[key], oldRow?.[key])
      );
      if (!changed.length) return oldRow;

      // Only the touched fields are validated. Rows routinely hold values that
      // fail a guessed subtype elsewhere, and blocking an unrelated edit on
      // those would make the grid unusable.
      const errors = validateRecord(schema, newRow);
      const blocking = changed.filter((key) => errors[key]);
      if (blocking.length) {
        toast.error(errors[blocking[0]]);
        throw new Error(errors[blocking[0]]);
      }

      if (props.demoMode) {
        // Reverting keeps the demo honest: the cell shows what the database
        // still holds rather than an edit that was never written.
        toast.success("Demo only. The demo database is not modified.");
        return oldRow;
      }

      props.onRowUpdate?.(newRow, oldRow);
      return newRow;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schema, props.onRowUpdate, props.demoMode]
  );

  const expandableFieldsMap = useMemo<
    Map<
      string,
      { relationName: string; path: string; level: number; parentPath?: string }
    >
  >(() => {
    if (props.expandableFieldsMap) return props.expandableFieldsMap;

    const expandable = new Map<
      string,
      { relationName: string; path: string; level: number; parentPath?: string }
    >();
    const currentData = props.data || [];

    schema.forEach((field) => {
      const match = field.name.match(/^(.+?)(Id|_id)$/);
      if (match) {
        expandable.set(field.name, {
          relationName: match[1],
          path: match[1],
          level: 0,
        });
      }
    });

    if (currentData.length > 0) {
      const sampleRow = currentData[0];
      Object.keys(sampleRow).forEach((key) => {
        const value = sampleRow[key];
        if (value && typeof value === "object" && !Array.isArray(value)) {
          Object.keys(value).forEach((nestedField) => {
            const match = nestedField.match(/^(.+?)(Id|_id)$/);
            if (match) {
              expandable.set(`${key}.${nestedField}`, {
                relationName: match[1],
                path: `${key}.${match[1]}`,
                level: key.split(".").length,
                parentPath: key,
              });
            }
          });
        }
      });
    }

    return expandable;
  }, [props.expandableFieldsMap, schema, props.data]);

  const data = useMemo(() => {
    const rows = props.data || [];
    return rows.map((row) => {
      const flattenedRow = { ...row };
      expandableFieldsMap.forEach(({ path }) => {
        if (!expandedFields.has(path)) return;
        const nestedObj = row[path];
        if (nestedObj && typeof nestedObj === "object") {
          getDisplayableFields(nestedObj).forEach((subField) => {
            flattenedRow[`__expanded_${path}_${subField}`] = nestedObj[subField];
          });
        }
      });
      return flattenedRow;
    });
  }, [props.data, expandedFields, expandableFieldsMap, getDisplayableFields]);

  const toggleExpansion = useCallback(
    (relationPath: string) => {
      if (!props.onExpansionChange) return;
      const next = new Set(props.expandableFields || []);
      if (next.has(relationPath)) next.delete(relationPath);
      else next.add(relationPath);
      props.onExpansionChange(Array.from(next));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.onExpansionChange, props.expandableFields]
  );

  /** Sub-columns produced by an expanded relation, at any nesting level. */
  const buildExpandedColumns = useCallback(
    (relationPath: string): GridColDef[] => {
      const sampleRow = data[0];
      if (!sampleRow) return [];
      const relatedObject = sampleRow[relationPath];
      if (!relatedObject || typeof relatedObject !== "object") return [];

      return getDisplayableFields(relatedObject).map((subField) => {
        const nestedInfo = expandableFieldsMap.get(`${relationPath}.${subField}`);
        const nestedExpanded = nestedInfo
          ? expandedFields.has(nestedInfo.path)
          : false;
        const headerName = `${relationPath
          .split(".")
          .map((p) => fieldLabel({ name: p }))
          .join(" › ")} › ${fieldLabel({ name: subField })}`;

        return {
          field: `__expanded_${relationPath}_${subField}`,
          headerName,
          description: `From the ${relationPath} relation`,
          hideable: !isSingleRow,
          flex: nestedInfo ? 0.4 : 1,
          minWidth: minWidthForHeader(headerName, nestedInfo ? 72 : 150),
          editable: false,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          cellClassName: "od-expanded-cell",
          headerClassName: "od-expanded-header",
          renderCell: (params: any) => {
            if (
              !nestedInfo ||
              params.value === null ||
              params.value === undefined
            ) {
              return <DataCell value={params?.value} />;
            }
            const relationLabel = fieldLabel({ name: nestedInfo.relationName });
            return (
              <Tooltip
                title={`${
                  nestedExpanded ? "Collapse" : "Expand"
                } ${relationLabel}`}
                placement="top-start"
              >
                <span
                  className="inline-flex w-full cursor-pointer items-center gap-1.5"
                  role="button"
                  tabIndex={0}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleExpansion(nestedInfo.path);
                  }}
                  onKeyDown={(event) => {
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    event.stopPropagation();
                    toggleExpansion(nestedInfo.path);
                  }}
                >
                  <LinkIcon
                    sx={{
                      fontSize: 13,
                      flexShrink: 0,
                      color: nestedExpanded
                        ? "var(--od-primary)"
                        : "var(--od-muted)",
                    }}
                  />
                  <span className="truncate">{String(params.value)}</span>
                </span>
              </Tooltip>
            );
          },
        } as GridColDef;
      });
    },
    [
      data,
      expandableFieldsMap,
      expandedFields,
      getDisplayableFields,
      isSingleRow,
      toggleExpansion,
    ]
  );

  const columns: GridColDef[] = useMemo(() => {
    // The Overview card hides the toolbar, so a row menu and a checkbox have
    // nothing to drive. Leaving them on spent the width the money column needs.
    const actionColumn: GridColDef[] = isSingleRow || props.hidePagination
      ? []
      : [
            {
              field: "actions",
              headerName: "",
              width: actionWidth,
              sortable: false,
              filterable: false,
              hideable: false,
              disableColumnMenu: true,
              disableReorder: true,
              align: "center",
              headerAlign: "center",
              resizable: false,
              cellClassName: "od-actions-cell",
              headerClassName: "od-actions-header",
              renderCell: (params: any) => (
                <RowActions
                  compact={compactActions}
                  readOnly={hideWriteChrome}
                  pendingCreate={hasUnsyncedCreate(params.row)}
                  onView={() => setDetailRow(params.row)}
                  onEdit={() => {
                    if (hasUnsyncedCreate(params.row)) return;
                    openForm(params.row);
                    props.onEdit?.(params.row);
                  }}
                  onDuplicate={() => duplicateRow(params.row)}
                  onDelete={() => {
                    if (hasUnsyncedCreate(params.row)) return;
                    props.onDelete?.(params.row);
                  }}
                />
              ),
            },
          ];

    const syncColumn: GridColDef[] = props.syncReady && props.syncChanges
      ? [
          {
            field: "__sync",
            headerName: "Sync",
            width: 170,
            minWidth: 140,
            sortable: false,
            filterable: false,
            hideable: false,
            disableColumnMenu: true,
            disableReorder: true,
            disableExport: true,
            cellClassName: "od-sync-cell",
            headerClassName: "od-sync-header",
            renderCell: (params: any) => (
              <SyncStatusMarker
                change={props.syncChanges?.get(rowKeyOrHash(params.row, idField)) ?? null}
                spreadsheetId={props.syncSpreadsheetId ?? null}
                onPopoverOpenChange={setSyncPopoverOpen}
                onRetry={props.onSyncRetry}
                onDismiss={props.onSyncDismiss}
                viewOnly={!!props.readOnly}
              />
            ),
          },
        ]
      : [];

    const dataColumns: GridColDef[] = [];

    // Rank the schema first, then emit each column with its expanded children
    // immediately after it. Ranking the flat list afterwards scattered the
    // relation sub-columns away from the key they belong to.
    const orderedSchema = schema
      .map((field, index) => ({ field, index }))
      .sort((a, b) => {
        const ra = columnRank(a.field);
        const rb = columnRank(b.field);
        return ra === rb ? a.index - b.index : ra - rb;
      })
      .map(({ field }) => field)
      // A password / token / api_key column has no readable value: the grid
      // would print the stored hash to anyone who can see the table, guests
      // included. EntityPicker already drops these from its preview; the grid
      // is the surface that actually renders every row, so it drops them too.
      .filter((field) => !isSecretField(field));

    orderedSchema.forEach((field) => {
      const expandInfo = expandableFieldsMap.get(field.name);
      const relationPath = expandInfo?.path;
      const isExpanded = !!relationPath && expandedFields.has(relationPath);
      const label = fieldLabel(field);
      const size = sizeFor(
        field,
        !!expandInfo,
        desktopPagination,
        data.map((row) => formatValue(row[field.name], field)),
        !!props.hidePagination,
      );
      const headerName = props.debugMode
        ? `${label} (${field.type}→${field.subType})`
        : label;
      const editable =
        !props.readOnly &&
        !isSingleRow &&
        !isReadOnlyField(field) &&
        !expandInfo &&
        !isUnsafeInlineEdit(field);
      if (touchTargets && editable && isBooleanField(field)) {
        size.minWidth = Math.max(size.minWidth, 120);
      }

      dataColumns.push({
        field: field.name,
        headerName,
        description: describeField(field),
        hideable: !isSingleRow,
        ...columnBox(size, headerName),
        // Only plain scalars are safely editable in place. Relation keys go
        // through the form, which validates them. Password is opted out.
        // Every other kind uses GridEditCell via inputKindFor. A create that
        // has not landed has no row to address yet (ARD 9.4).
        editable,
        sortable: !isSingleRow,
        filterable: false,
        disableColumnMenu: isSingleRow,
        headerAlign: isNumeric(field) ? "right" : "left",
        align: isNumeric(field) ? "right" : "left",
        ...gridColEditProps(field),
        cellClassName: isNumeric(field) ? "od-numeric-cell" : undefined,
        renderCell: (params: any) => {
          if (expandInfo && relationPath) {
            const related = params.row[relationPath];
            const hasRelated = related && typeof related === "object";
            if (params.value === null || params.value === undefined) {
              return <DataCell value={null} />;
            }
            const relationLabel = fieldLabel({
              name: expandInfo.relationName,
            });
            return (
              <Tooltip
                title={
                  isSingleRow
                    ? hasRelated
                      ? `Linked ${relationLabel}`
                      : "No linked record found"
                    : `${isExpanded ? "Collapse" : "Expand"} ${relationLabel}`
                }
                placement="top-start"
              >
                <span
                  className={`inline-flex w-full items-center gap-1.5${
                    isSingleRow ? "" : " cursor-pointer"
                  }`}
                  role={isSingleRow ? undefined : "button"}
                  tabIndex={isSingleRow ? undefined : 0}
                  onClick={
                    isSingleRow
                      ? undefined
                      : (event) => {
                          event.stopPropagation();
                          toggleExpansion(relationPath);
                        }
                  }
                  onKeyDown={
                    isSingleRow
                      ? undefined
                      : (event) => {
                          if (event.key !== "Enter" && event.key !== " ") return;
                          event.preventDefault();
                          event.stopPropagation();
                          toggleExpansion(relationPath);
                        }
                  }
                >
                  <LinkIcon
                    sx={{
                      fontSize: 13,
                      flexShrink: 0,
                      color: isExpanded ? "var(--od-primary)" : "var(--od-muted)",
                      opacity: hasRelated ? 1 : 0.4,
                    }}
                  />
                  <span className="truncate">{String(params.value)}</span>
                </span>
              </Tooltip>
            );
          }
          return <DataCell value={params.value} field={field} />;
        },
      });

      if (isExpanded && relationPath) {
        dataColumns.push(...buildExpandedColumns(relationPath));
      }
    });

    // Nested expansions (job.availability) have no schema column to hang off.
    const anchored = new Set(
      schema
        .map((f) => expandableFieldsMap.get(f.name)?.path)
        .filter(Boolean) as string[]
    );
    Array.from(expandedFields)
      .filter((path) => path.includes(".") && !anchored.has(path))
      .forEach((path) => {
        dataColumns.push(...buildExpandedColumns(path));
      });

    const body = [...actionColumn, ...dataColumns];

    if (columnOrder.length > 0) {
      body.sort((a, b) => {
        const ai = columnOrder.indexOf(a.field);
        const bi = columnOrder.indexOf(b.field);
        if (ai === -1 && bi === -1) return 0;
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      });
    }

    // Always last: sorting happens before the spacer joins, so a saved column
    // order can never drag the gutter into the middle of the table.
    // The status column sits first so a narrowed grid still shows it. It is
    // chrome, then actions, then the data.
    return [...syncColumn, ...body, gutterColumn];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    schema,
    buildExpandedColumns,
    data,
    openForm,
    duplicateRow,
    props.debugMode,
    props.onEdit,
    props.onDelete,
    isSingleRow,
    compactActions,
    actionWidth,
    touchTargets,
    hideWriteChrome,
    columnOrder,
    expandedFields,
    expandableFieldsMap,
    toggleExpansion,
    desktopPagination,
    props.hidePagination,
    props.syncReady,
    props.syncChanges,
    props.syncSpreadsheetId,
    setSyncPopoverOpen,
    props.onSyncRetry,
    props.onSyncDismiss,
    idField,
    hasUnsyncedCreate,
  ]);

  const effectiveColumnVisibility = useMemo(() => {
    const base = (() => {
      if (desktopPagination) return columnVisibility;
      const mobileVisibility = { ...columnVisibility };
      schema.forEach((field) => {
        if (
          isPrimaryKey(field) &&
          !Object.prototype.hasOwnProperty.call(mobileVisibility, field.name)
        ) {
          mobileVisibility[field.name] = false;
        }
      });
      return mobileVisibility;
    })();
    if (!props.hidePagination || overviewClipHides.length === 0) return base;
    const fitted = { ...base };
    overviewClipHides.forEach((field) => {
      fitted[field] = false;
    });
    return fitted;
  }, [columnVisibility, desktopPagination, overviewClipHides, props.hidePagination, schema]);

  useLayoutEffect(() => {
    if (!gridHostReady || !props.hidePagination) return;
    const host = gridHostRef.current;
    if (!host) return;
    let frame = 0;
    const fit = () => {
      const main = host.querySelector(".MuiDataGrid-main");
      if (!main) return;
      const headers = Array.from(
        host.querySelectorAll(".MuiDataGrid-columnHeader")
      ).map((el) => {
        const title = el.querySelector(".MuiDataGrid-columnHeaderTitle");
        const rect = (title ?? el).getBoundingClientRect();
        return {
          field: el.getAttribute("data-field") || "",
          left: rect.left,
          right: rect.right,
          text: title?.textContent || "",
        };
      });
      const next = overviewColumnsToHide(
        headers,
        main.getBoundingClientRect().right,
        columns.map((col) => col.field)
      );
      if (next.length === 0) return;
      setOverviewClipHides((prev) => {
        const merged = Array.from(new Set(prev.concat(next)));
        return merged.join("|") === prev.join("|") ? prev : merged;
      });
    };
    fit();
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(fit);
    });
    ro.observe(host);
    const fonts = document.fonts;
    if (fonts) fonts.ready.then(fit);
    return () => {
      cancelAnimationFrame(frame);
      ro.disconnect();
    };
  }, [columns, gridHostReady, props.hidePagination]);

  const { onSortModelChange, onColumnVisibilityChange, hidePagination } = props;
  const handleColumnVisibilityChange = useCallback(
    (newModel: { [key: string]: boolean }) => {
      // Overview fit hides are local. Writing them back would put
      // hiddenColumns on the dashboard URL.
      if (hidePagination) return;
      if (desktopPagination) {
        onColumnVisibilityChange(newModel);
        return;
      }

      const normalized = { ...newModel };
      schema.forEach((field) => {
        if (
          isPrimaryKey(field) &&
          effectiveColumnVisibility[field.name] === false &&
          !Object.prototype.hasOwnProperty.call(normalized, field.name)
        ) {
          normalized[field.name] = true;
        }
      });
      onColumnVisibilityChange(normalized);
    },
    [
      desktopPagination,
      effectiveColumnVisibility,
      onColumnVisibilityChange,
      hidePagination,
      schema,
    ]
  );

  const usingSyntheticIds =
    !!idField && ["_syntheticId", "_ctid", "_rowNumber"].includes(idField);

  // Bulk writes are withheld from tables without a real primary key: the row id
  // is then a ctid or an ordinal, and updating a batch by those would target
  // whatever rows happen to sit in those positions now.
  const selectionEnabled =
    !props.hidePagination &&
    (!!props.onBulkDelete || !!props.onBulkEdit) &&
    !isSingleRow &&
    !usingSyntheticIds;
  const selectedCount = props.selectedIds?.length ?? 0;
  const matchingSelectCap = props.matchingSelectCap ?? 500;

  /* ------------------------------------------------------- context menu */

  const rowIdOf = useCallback(
    (row: any): string => rowKeyOrHash(row, idField),
    [idField]
  );

  const selectedIdSet = useMemo(
    () => new Set((props.selectedIds ?? []).map(String)),
    [props.selectedIds]
  );
  const pageIdSet = useMemo(
    () => new Set(data.map(rowIdOf)),
    [data, rowIdOf]
  );
  const allPageSelected =
    data.length > 0 && data.every((row) => selectedIdSet.has(rowIdOf(row)));
  const matchingAlready =
    selectedCount >= Math.min(props.total, matchingSelectCap);
  const showSelectMatching =
    !!props.onSelectMatching &&
    allPageSelected &&
    props.total > data.length &&
    !matchingAlready;
  const keepNonExistentRowsSelected = (props.selectedIds ?? []).some(
    (id) => !pageIdSet.has(String(id))
  );
  const selectMatchingLabel =
    props.total > matchingSelectCap
      ? `Select first ${matchingSelectCap} matching records`
      : "Select all matching records";

  const [menu, setMenu] = useState<{
    position: ContextMenuPosition;
    row?: any;
    field?: string;
  } | null>(null);

  const closeMenu = useCallback(() => setMenu(null), []);

  const copy = useCallback((text: string, what: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => toast.success(`${what} copied`))
      .catch(() => toast.error("Clipboard is blocked in this browser"));
  }, []);

  const sortBy = useCallback(
    (field: string, sort: "asc" | "desc" | null) => {
      onSortModelChange?.(sort ? [{ field, sort }] : []);
    },
    [onSortModelChange]
  );

  const hideColumn = useCallback(
    (field: string) => {
      onColumnVisibilityChange({ ...columnVisibility, [field]: false });
    },
    [columnVisibility, onColumnVisibilityChange]
  );

  /**
   * Right-clicking chrome (scrollbars, the footer, empty space) should keep the
   * browser's own menu; only cells and column headers get ours.
   */
  const handleContextMenu = useCallback(
    (event: React.MouseEvent) => {
      const target = event.target as HTMLElement;
      const cell = target.closest<HTMLElement>(".MuiDataGrid-cell");
      const header = target.closest<HTMLElement>(".MuiDataGrid-columnHeader");
      if (!cell && !header) return;

      const field = (cell ?? header)?.getAttribute("data-field") ?? undefined;
      if (field === "__check__") return;

      const rowElement = target.closest<HTMLElement>(".MuiDataGrid-row");
      const rowId = rowElement?.getAttribute("data-id");
      const row =
        rowId != null
          ? (props.data || []).find((candidate) => rowIdOf(candidate) === rowId)
          : undefined;

      event.preventDefault();
      setMenu({
        position: { mouseX: event.clientX + 2, mouseY: event.clientY - 4 },
        row,
        field: field === "actions" ? undefined : field,
      });
    },
    [props.data, rowIdOf]
  );

  const menuEntries = useMemo<ContextMenuEntry[]>(() => {
    if (!menu) return [];

    const entries: ContextMenuEntry[] = [];
    const field = menu.field;
    // Expanded relation columns are `relation.column` paths that no backend
    // filter or sort understands, so they only get the copy actions.
    const schemaField = field
      ? schema.find((f) => f.name === field)
      : undefined;
    const label = schemaField
      ? fieldLabel(schemaField)
      : field
        ? fieldLabel({ name: field } as Field)
        : undefined;
    const value = field && menu.row ? menu.row[field] : undefined;
    const rendered =
      value === undefined ? "" : formatValue(value, schemaField as Field);
    const filterable =
      !!field &&
      !!schemaField &&
      !!props.onAddFilter &&
      value !== null &&
      value !== undefined &&
      (typeof value !== "object" || value instanceof Date);

    if (menu.row) {
      if (field) {
        entries.push({
          kind: "caption",
          label: label as string,
          detail: rendered === "" ? "(empty)" : rendered,
        });
      }

      entries.push({
        kind: "item",
        label: "View record",
        icon: <VisibilityOutlinedIcon />,
        onClick: () => setDetailRow(menu.row),
      });
      if (!hideWriteChrome) {
        entries.push({
          kind: "item",
          label: "Edit record",
          icon: <EditOutlinedIcon />,
          onClick: () => {
            openForm(menu.row);
            props.onEdit?.(menu.row);
          },
        });
      }
      if (!hideWriteChrome) {
        entries.push({
          kind: "item",
          label: "Duplicate as new",
          icon: <ContentCopyOutlinedIcon />,
          onClick: () => duplicateRow(menu.row),
        });
      }
      entries.push({ kind: "divider" });

      if (field) {
        entries.push({
          kind: "item",
          label: "Copy cell value",
          icon: <ContentCopyOutlinedIcon />,
          disabled: value === null || value === undefined,
          onClick: () =>
            copy(
              typeof value === "object" ? JSON.stringify(value) : String(value),
              label as string
            ),
        });
      }

      entries.push({
        kind: "item",
        label: "Copy row as JSON",
        icon: <DataObjectIcon />,
        onClick: () => {
          const clean = Object.fromEntries(
            Object.entries(menu.row).filter(([key]) => !key.startsWith("_"))
          );
          copy(JSON.stringify(clean, null, 2), "Row");
        },
      });
    } else if (field) {
      entries.push({ kind: "caption", label: label as string, detail: field });
    }

    if (field) {
      if (filterable) {
        const filterValue =
          typeof value === "boolean"
            ? value
            : value instanceof Date
              ? value.toISOString()
              : String(value);
        entries.push(
          { kind: "divider" },
          {
            kind: "item",
            label: `Filter ${label} = this value`,
            icon: <FilterAltOutlinedIcon />,
            onClick: () => props.onAddFilter?.(field, "==", filterValue),
          },
          {
            kind: "item",
            label: `Filter ${label} ≠ this value`,
            icon: <FilterAltOffOutlinedIcon />,
            onClick: () => props.onAddFilter?.(field, "!=", filterValue),
          }
        );
      }

      if (schemaField) {
        const current = props.sortModel?.[0];
        entries.push(
          { kind: "divider" },
          {
            kind: "item",
            label: "Sort ascending",
            icon: <ArrowUpwardIcon />,
            disabled: current?.field === field && current?.sort === "asc",
            onClick: () => sortBy(field, "asc"),
          },
          {
            kind: "item",
            label: "Sort descending",
            icon: <ArrowDownwardIcon />,
            disabled: current?.field === field && current?.sort === "desc",
            onClick: () => sortBy(field, "desc"),
          }
        );
        if (current?.field === field) {
          entries.push({
            kind: "item",
            label: "Clear sort",
            icon: <SortIcon />,
            onClick: () => sortBy(field, null),
          });
        }
      }

      entries.push({
        kind: "item",
        label: `Hide ${label}`,
        icon: <VisibilityOffOutlinedIcon />,
        onClick: () => hideColumn(field),
      });

      if (!menu.row) {
        entries.push({
          kind: "item",
          label: "Copy column name",
          icon: <ContentCopyOutlinedIcon />,
          onClick: () => copy(field, "Column name"),
        });
      }
    }

    if (menu.row && props.onDelete && !hideWriteChrome) {
      entries.push(
        { kind: "divider" },
        {
          kind: "item",
          label: "Delete record",
          icon: <DeleteOutlineIcon />,
          danger: true,
          onClick: () => props.onDelete?.(menu.row),
        }
      );
    }

    return entries;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    menu,
    schema,
    props.onAddFilter,
    props.onDelete,
    props.onEdit,
    hideWriteChrome,
    props.sortModel,
    openForm,
    duplicateRow,
    copy,
    sortBy,
    hideColumn,
  ]);


  const noRowsOverlay = useCallback(
    () =>
      props.emptySuppressed ? null : props.accessNotice ? (
        <Overlay
          icon={<InfoOutlinedIcon sx={{ fontSize: 30 }} />}
          title={props.accessNotice}
        />
      ) : props.hasActiveFilters ? (
        <Overlay
          icon={<SearchOffIcon sx={{ fontSize: 30 }} />}
          title="No rows match these filters"
          hint={
            props.onClearFilters ? (
              <Button
                onClick={props.onClearFilters}
                variant="secondary" size="sm" className="mt-1 text-od-primary-text"
              >
                Clear all filters
              </Button>
            ) : (
              "Loosen or remove a filter to see rows."
            )
          }
        />
      ) : (
        <Overlay
          icon={<TableRowsIcon sx={{ fontSize: 30 }} />}
          title={`${props.collection || "This table"} is empty`}
          hint={
            props.readOnly
              ? undefined
              : "Add the first record to get started."
          }
        />
      ),
    [props.emptySuppressed, props.accessNotice, props.hasActiveFilters, props.onClearFilters, props.collection, props.readOnly]
  );

  const hugHeight = gridHugHeight(data.length, metrics, isSingleRow);
  const [paneCap, setPaneCap] = useState<number | null>(null);
  const gridHeight =
    paneCap == null ? hugHeight : Math.min(hugHeight, paneCap);

  useLayoutEffect(() => {
    if (gridHostReady) return;
    const host = gridHostRef.current;
    if (!host) return;
    const publish = () => {
      if (host.clientWidth > 0) setGridHostReady(true);
    };
    publish();
    const ro = new ResizeObserver(publish);
    ro.observe(host);
    return () => {
      ro.disconnect();
    };
  }, [gridHostReady]);

  // Cap only against an ancestor with an absolute max-height (overview
  // max-h-[420px]). Measuring [data-od-table-pane] while that pane is h-min
  // tracked the host itself: ResizeObserver then raised height ~1px/frame
  // when growing (search clear, larger page size).
  useLayoutEffect(() => {
    if (isSingleRow) return;
    const host = gridHostRef.current;
    if (!host) return;
    const update = () => setPaneCap(measureGridHostCap(host));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(host);
    let el: HTMLElement | null = host.parentElement;
    while (el && el !== document.body) {
      ro.observe(el);
      const maxHeight = getComputedStyle(el).maxHeight;
      if (
        maxHeight !== "none" &&
        maxHeight !== "auto" &&
        !maxHeight.endsWith("%")
      ) {
        break;
      }
      el = el.parentElement;
    }
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", update);
    };
  }, [hugHeight, isSingleRow, selectedCount, usingSyntheticIds]);

  useLayoutEffect(() => {
    if (!gridHostReady || !props.hidePagination || isSingleRow) return;
    const host = gridHostRef.current;
    if (!host) return;
    const scroller = host.querySelector(".MuiDataGrid-virtualScroller");
    if (!(scroller instanceof HTMLElement)) return;
    const box = scroller.getBoundingClientRect();
    const rows = Array.from(host.querySelectorAll(".MuiDataGrid-row")).map(
      (el) => {
        const row = el.getBoundingClientRect();
        return { top: row.top, bottom: row.bottom };
      }
    );
    const shrink = overviewRowClipShrink(box.bottom, rows, rowHeight);
    if (shrink > 1) {
      scroller.style.maxHeight = `${Math.max(rowHeight, box.height - shrink)}px`;
    }
  }, [data.length, gridHeight, gridHostReady, isSingleRow, props.hidePagination, rowHeight]);

  return (
    <div
      ref={tableContainerRef}
      onContextMenu={isSingleRow ? undefined : handleContextMenu}
      onKeyDownCapture={(event) => {
        if (!syncPopoverOpenRef.current) return;
        if (
          event.key === "Escape" ||
          event.key === "Tab" ||
          event.key.startsWith("Arrow")
        ) {
          // The dialog element still gets the event; the grid does not.
          event.stopPropagation();
        }
      }}
      className="relative flex min-h-0 min-w-0 w-full flex-col bg-od-surface"
    >
      <ContextMenu
        position={menu?.position ?? null}
        entries={menuEntries}
        onClose={closeMenu}
      />
      {usingSyntheticIds && !isSingleRow && (
        <div
          className="flex items-center gap-2 border-b px-4 py-2 text-xs"
          style={{
            color: "var(--od-warning)",
            borderColor: "var(--od-border)",
            backgroundColor:
              "color-mix(in srgb, var(--od-warning) 10%, transparent)",
          }}
        >
          This table has no primary key, so editing and deleting rows may not
          target the row you expect.
        </div>
      )}
      {!hideWriteChrome && FormModal}
      <RowDetailModal
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        row={detailRow}
        schema={schema}
        title={props.collection ? fieldLabel({ name: props.collection }) : "Record"}
        // MOSA-368 slice 1. The History tab asks the API for this row, so it
        // needs the same id the data routes take. A card with no datasource or
        // a row with no identity gets no tab rather than an empty one.
        datasourceId={props.datasourceId}
        collection={props.collection}
        rowKey={detailRow ? rowKey(detailRow, idField) ?? null : null}
        actions={
          <>
              {!hideWriteChrome && (
              <Button
                onClick={() => {
                  const row = detailRow;
                  setDetailRow(null);
                  if (row) duplicateRow(row);
                }}
                variant="secondary" size="sm"
              >
                Duplicate
              </Button>
              )}
              {!hideWriteChrome && (
              <Button
                onClick={() => {
                  const row = detailRow;
                  setDetailRow(null);
                  if (row) openForm(row);
                }}
                variant="secondary" size="sm"
              >
                Edit
              </Button>
              )}
          </>
        }
      />
      {selectionEnabled && selectedCount > 0 && (
        <div
          className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-b px-4 py-2 text-xs"
          style={{
            borderColor: "var(--od-border)",
            backgroundColor:
              "color-mix(in srgb, var(--od-primary) 8%, transparent)",
          }}
        >
          <span className="font-semibold text-od-text">
            {selectedCount} selected
          </span>
          <Button
            onClick={() => props.onSelectedIdsChange?.([])}
            variant="ghost" size="sm"
          >
            Clear
          </Button>
          {showSelectMatching && (
            <Button
              onClick={props.onSelectMatching}
              disabled={props.selectMatchingPending}
              variant="secondary" size="sm" className="text-od-primary-text"
            >
              {props.selectMatchingPending
                ? "Selecting…"
                : selectMatchingLabel}
            </Button>
          )}
          <div className="ml-auto flex min-w-0 flex-wrap items-center gap-2">
          {props.onBulkExport && (
            <Button
              onClick={props.onBulkExport}
              variant="secondary" size="sm"
            >
              Export {selectedCount}{" "}
              {selectedCount === 1 ? "record" : "records"}
            </Button>
          )}
          {props.onBulkEdit && (
            <Button
              onClick={props.onBulkEdit}
              variant="secondary" size="sm"
            >
              Edit {selectedCount}{" "}
              {selectedCount === 1 ? "record" : "records"}
            </Button>
          )}
          {props.onBulkDelete && (
          <Button
            onClick={props.onBulkDelete}
            variant="danger-solid" size="sm"
          >
            Delete {selectedCount} {selectedCount === 1 ? "record" : "records"}
          </Button>
          )}
          </div>
        </div>
      )}
      <div
        ref={gridHostRef}
        className="min-h-0 min-w-0 w-full"
        style={
          isSingleRow
            ? { flex: 1, minHeight: 0 }
            : desktopPagination
              ? { height: gridHeight }
              : undefined
        }
      >
      {desktopPagination || isSingleRow ? (
      gridHostReady && (
      <DataGrid
        rows={data}
        columns={columns}
        paginationMode={props.paginationMode}
        rowCount={props.paginationMode === "server" ? props.total : undefined}
        pageSizeOptions={[...GRID_PAGE_SIZES]}
        paginationModel={isSingleRow ? undefined : paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        sortingMode={props.paginationMode === "server" ? "server" : "client"}
        sortModel={props.sortModel}
        onSortModelChange={props.onSortModelChange}
        loading={props.loading}
        rowHeight={rowHeight}
        columnHeaderHeight={headerHeight}
        getRowId={(row) => rowKeyOrHash(row, idField)}
        onRowClick={props.onRowClick}
        onRowDoubleClick={(params, event, details) => {
          if (!isSingleRow && !hideWriteChrome) openForm(params.row);
          props.onRowDoubleClick?.(params.row, event, details);
        }}
        processRowUpdate={handleProcessRowUpdate}
        // Thrown validation errors already surfaced as a toast; without this
        // handler MUI logs an unhandled rejection for each one.
        onProcessRowUpdateError={() => undefined}
        columnVisibilityModel={effectiveColumnVisibility}
        onColumnVisibilityModelChange={handleColumnVisibilityChange}
        onColumnOrderChange={(params: GridColumnOrderChangeParams) => {
          if (!props.onColumnOrderChange) return;
          const newOrder = columns.map((col) => col.field);
          const [moved] = newOrder.splice(params.oldIndex, 1);
          newOrder.splice(params.targetIndex, 0, moved);
          // The spacer is chrome, not a column the user owns; keep it out of
          // the persisted order.
          props.onColumnOrderChange(
            newOrder.filter((field) => field !== GUTTER_FIELD)
          );
        }}
        checkboxSelection={selectionEnabled}
        className={
          props.hidePagination
            ? "od-overview-grid od-overview-fitted od-overview-rowfit"
            : undefined
        }
        rowSelectionModel={props.selectedIds}
        onRowSelectionModelChange={props.onSelectedIdsChange}
        keepNonExistentRowsSelected={keepNonExistentRowsSelected}
        disableRowSelectionOnClick
        slots={{
          toolbar: null,
          noRowsOverlay,
          pagination: DataTablePagination,
        }}
        hideFooter={!!props.hidePagination}
        sx={{
          ...gridSx(isSingleRow, desktopPagination, metrics, !!props.hidePagination),
          height: "100%",
          maxHeight: "100%",
        }}
      />
      )
      ) : data.length === 0 ? (
        noRowsOverlay()
      ) : (
        <>
          <MobileRecordList
            rows={data}
            schema={schema}
            idField={idField}
            loading={props.loading}
            onOpen={setDetailRow}
          />
          {!props.hidePagination && (
            <MobilePageBar
              page={paginationModel.page}
              pageSize={paginationModel.pageSize}
              total={props.total}
              onChange={onPaginationModelChange}
            />
          )}
        </>
      )}
      </div>
    </div>
  );
};

const describeField = (field: Field): string => {
  const bits: string[] = [field.name];
  if (field.type) bits.push(field.subType ? `${field.type} · ${field.subType}` : field.type);
  if (field.nullable === false) bits.push("required");
  if ((field as Field).primary) bits.push("primary key");
  return bits.join(" · ");
};

/**
 * The free DataGrid has no column pinning, so the actions column is pinned with
 * plain sticky positioning instead. It needs an opaque background or rows scroll
 * underneath it.
 */
const gridSx = (
  isSingleRow: boolean,
  desktopPagination: boolean,
  metrics: ReturnType<typeof gridMetrics>,
  hideFooter = false
) => ({
  border: "none",
  boxShadow: "none",
  backgroundColor: "var(--od-surface)",
  color: "var(--od-text)",
  "--DataGrid-containerBackground": "var(--od-surface-2)",

  "& .MuiDataGrid-columnHeaders": {
    borderBottom: "1px solid var(--od-border)",
  },
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "var(--od-surface-2)",
    overflow: "hidden",
  },
  "& .MuiDataGrid-columnHeaderTitle": {
    fontWeight: 600,
    fontSize: "0.75rem",
    letterSpacing: "0.02em",
    textTransform: "uppercase",
    color: "var(--od-muted)",
    // Uppercasing widens the label past its column, and a right-aligned
    // numeric header then ran into its neighbour.
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  "& .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-columnHeaderTitleContainerContent":
    { minWidth: 0, overflow: "hidden" },
  "& .MuiDataGrid-columnSeparator": { color: "var(--od-border)" },
  "& .MuiDataGrid-iconButtonContainer, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIcon":
    { color: "var(--od-muted)" },

  "& .MuiDataGrid-cell": {
    padding: isSingleRow ? `${GRID_SINGLE_CELL_PADDING_Y}px 12px` : `0 ${GRID_CELL_PADDING_X}px`,
    display: "flex",
    alignItems: "center",
    color: "var(--od-text)",
    borderBottom: `${GRID_CELL_BORDER}px solid var(--od-border)`,
    fontSize: "0.8125rem",
    minWidth: 0,
    "&:focus, &:focus-within": { outline: "none" },
  },
  "& .MuiDataGrid-cell > *": { minWidth: 0 },
  "& .MuiDataGrid-cellCheckbox, & .MuiDataGrid-columnHeaderCheckbox": {
    "@media (max-width: 639px), (pointer: coarse)": {
      padding: 0,
      "& .MuiCheckbox-root": { minWidth: 44, minHeight: 44 },
    },
  },
  // Let grouped numbers paint into the trailing gutter instead of clipping
  // their final digit at MUI's cell overflow boundary.
  "& .MuiDataGrid-cell.od-numeric-cell": {
    overflow: "visible",
    paddingRight: "12px",
  },

  // Give the trailing column a right gutter so the grid does not read as
  // clipped against the container edge. Columns use flex, so the last one sits
  // flush right; extra inner padding on the last cell/header lets it breathe.
  "& .MuiDataGrid-columnHeader:last-of-type": { paddingRight: "16px" },
  "& .MuiDataGrid-cell:last-of-type": {
    paddingRight: isSingleRow ? "24px" : "22px",
  },

  "& .MuiDataGrid-row:hover": {
    backgroundColor: isSingleRow ? "transparent" : "var(--od-surface-2)",
  },
  "& .MuiDataGrid-row:hover .od-actions-cell": {
    backgroundColor: "var(--od-surface-2)",
  },

  // Relation sub-columns get a subtle inset so it stays obvious which values
  // came from a join rather than from the row itself.
  "& .od-expanded-cell": {
    backgroundColor: "color-mix(in srgb, var(--od-primary) 5%, transparent)",
  },
  "& .od-expanded-header": {
    backgroundColor:
      "color-mix(in srgb, var(--od-primary) 9%, var(--od-surface-2))",
  },

  // The community DataGrid has no column pinning, and it virtualises columns
  // horizontally, so `position: sticky` cannot work either: the actions cell is
  // unmounted once it scrolls out. It stays the first column and scrolls with
  // the rest; double-clicking any row opens the same editor.
  "& .od-actions-cell": {
    backgroundColor: "var(--od-surface)",
    borderRight: `${GRID_CELL_BORDER}px solid var(--od-border)`,
  },
  "& .od-actions-header": {
    backgroundColor: "var(--od-surface-2)",
    borderRight: "1px solid var(--od-border)",
  },

  // Trailing spacer column: empty cells, no separator, no pointer target.
  "& .od-gutter-cell, & .od-gutter-header": {
    padding: 0,
    border: "none",
    pointerEvents: "none",
  },
  "& .od-gutter-header .MuiDataGrid-columnSeparator": { display: "none" },

  // The single overflow control must stay fully visible at every viewport.
  "& .od-row-actions": {
    opacity: 1,
    transition: "opacity 120ms ease",
  },
  "& .MuiDataGrid-row:hover .od-row-actions, & .od-row-actions:focus-within": {
    opacity: 1,
  },

  "& .MuiDataGrid-footerContainer": isSingleRow || hideFooter
    ? { display: "none" }
    : {
        backgroundColor: "var(--od-surface)",
        borderTop: "1px solid var(--od-border)",
        minHeight: `${metrics.footerHeight}px`,
        ...(desktopPagination
          ? {}
          : {
              height: `${metrics.footerHeight}px`,
              maxHeight: `${metrics.footerHeight}px`,
            }),
      },
  "& .MuiDataGrid-overlay": { backgroundColor: "var(--od-surface)" },
  // The horizontal scrollbar is drawn inside the scroller, so without this the
  // last row sits underneath it. Do not set minHeight here: it inflated short
  // pages into a band of empty cells above the footer.
  "& .MuiDataGrid-virtualScroller": {
    paddingBottom: isSingleRow ? 0 : "10px",
  },
  // MOSA-545: the overview card has no footer, and overlay scrollbars (macOS,
  // and MUI measures them as 0px) paint nothing at rest, so a wide table read
  // as a clipped last column. Custom webkit scrollbar styles opt out of overlay
  // mode, so the bar is always drawn; Firefox gets the standard properties.
  ...(hideFooter && !isSingleRow
    ? {
        "& .MuiDataGrid-scrollbar--horizontal": {
          "--size": "10px",
          "&::-webkit-scrollbar": { height: "10px" },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "var(--od-surface-2)",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "var(--od-border-strong)",
            borderRadius: "999px",
            border: "2px solid var(--od-surface-2)",
          },
          "@supports not selector(::-webkit-scrollbar)": {
            scrollbarWidth: "thin",
            scrollbarColor: "var(--od-border-strong) var(--od-surface-2)",
          },
        },
      }
    : {}),
});

export const DynamicDataTable = React.memo(DynamicDataTableComponent);
